import * as transform from './transform';
import { isVerticalType, forAxisTicks, forAxisTicksReverse } from './util';
import { NBSP } from '../data/constants';
import { ANNOTATIONS_SPACE, ANNOTATIONS_SPACE_PRINT } from '../data/constants';
import * as element from '../util/element';

/**
 * Manipulates the SVG drawn by Highcharts to update the scale (Y) axis.
 */
export default function axesPostRenderYAxis(highchart, options, styles) {
  // The scale axis is always the Y axis, even on bar charts where the scale
  // axis visually is the X axis:
  // for wsj yaxis labels, set to tabular-nums
  if (!options.print && options.product === 'wsj') {
    highchart.container.getElementsByClassName('highcharts-yaxis-labels')[0].setAttribute('style', 'font-variant-numeric: tabular-nums;');
  }

  const axis = highchart.axes[1];
  if (isVerticalType(options.type)) {
    makeDecimalsConsistent(axis);
    extendTicks(axis, options);
  } else {
    shiftLastHorizontalBarLine(highchart, options);
  }
  setZeroLineToBlack(axis, options);

  insertZeroesForEmptyBars(highchart.renderer, axis, options);
  if (options.type === 'column' || options.type === 'bar') {
    moveZeroLineToTop(highchart.container, axis, options);
  }
  ensureLabelsFit(axis, options, highchart);

  if (__WSJ__) {
    if (options.product === 'wsj') {
      moveTopLabel(axis, options);
      
    }
  }

  addSuffix(highchart.renderer, axis, options, styles, highchart);
}

// Set line representing the zero value to black.
function setZeroLineToBlack(axis, options) {
  const zeroTick = axis.ticks[0];
  if (__MARKETWATCH__) {
    if (options.product === 'marketwatch' && options.type === 'column') {
      return;
    }
  }
  if (zeroTick) {
    zeroTick.gridLine.element.setAttribute('stroke', '#000');

    if(__WSJ__){
      if(options.product === 'wsj'){
        zeroTick.gridLine.element.setAttribute('stroke', options.styles.axes.zeroGridLine.color);
      }
    }
  }
}

function makeDecimalsConsistent(axis) {
  const zeroIndex = axis.tickPositions.indexOf(0);
  const maxDecimals = getMaxTicksDecimals(axis);
  if (maxDecimals > 0) {
    forAxisTicks(axis, (tick, key, index) => {
      let textStr = tick.label.textStr;
      let indexDot = textStr.indexOf('.');
      let decimals = (indexDot === -1) ? 0 : (textStr.length - indexDot - 1);
      if (key === 0) {
        textStr += '<tspan fill-opacity="0">';
      }
      if (indexDot === -1) {
        indexDot = textStr.length;
        textStr += '.';
      }
      while (decimals++ < maxDecimals) {
        textStr += '0';
      }
      if (key === 0) {
        textStr += '</tspan>';
      }
      tick.label.textStr = textStr;
      tick.label.element.innerHTML = textStr;
    });
  }
}

// Extend grid lines under the label text on the y-axis (for "vertical" axes).
function extendTicks(axis, options) {
  forAxisTicks(axis, tick => {
    const line = tick.gridLine.element;
    if (line.hasAttribute('d')) {
      const d = transform.read(line, 'd');
      d[0][0] = 0;
      if (options.spaceAddedForAnnotations) {
        d[1][0] += options.print ? ANNOTATIONS_SPACE_PRINT : ANNOTATIONS_SPACE;
      }
      transform.write(line, 'd', d);
      // const d = line.getAttribute('d').split(/\s/g);
      // d[1] = 0;
      // if (options.spaceAddedForAnnotations) {
      //   console.log('???');
      // }
      // line.setAttribute('d', d.join(' '));
    }
  });
}

function getMaxTicksDecimals(axis) {
  let maxDecimals = 0;
  forAxisTicks(axis, (tick, pos) => {
    const decimal = tick.label.textStr.split('.')[1];
    if (decimal !== undefined) {
      maxDecimals = Math.max(decimal.length, maxDecimals);
    }
  });
  return maxDecimals;
}

// Add units suffix string to the top tick label (`isLast`).
function addSuffix(renderer, axis, options, styles, highchart) {
  if (options.axes.units && isVerticalType(options.type)) {
    for (let key in axis.ticks) {
      const ok1 = options.axes.reversed && axis.ticks[key].isFirst;
      const ok2 = !options.axes.reversed && axis.ticks[key].isLast;
      if (ok1 || ok2) {
        let label = axis.ticks[key].label.element;

        // To align the 0 properly with other labels without having
        // decimals, the decimals are made inivisible. But this means
        // any units suffix must be offset to not show up to the right
        // of these invisible decimals but instead right next to the 0.
        const tspan = label.querySelector('tspan');
        let xOffset = 0;
        if (tspan && tspan.getAttribute('opacity') === '0') {
          let tspanInnerHTML = label.innerHTML + '';
          let labelFullWidth = label.getBoundingClientRect().width;
          label.innerHTML = '0';
          let labelTmpWidth = label.getBoundingClientRect().width;
          xOffset = labelTmpWidth - labelFullWidth;
          label.innerHTML = tspanInnerHTML;
        }

        const unitStr =  options.product === 'financialnews' ? options.axes.units.suffix : options.axes.units.suffix.replace(/^\s+/, NBSP);
        if ((options.type === 'line' || options.type === 'column') && options.product === 'marketwatch') {
          label.innerHTML = label.innerHTML + '<tspan>' + ' ' + unitStr + '</tspan>';
          break;
        }
        if ((options.type === 'line' || options.type === 'column') && options.product === 'financialnews') {
          if (options.axes.units.suffix !== "%") {
            const labelYVal = label.getAttribute('y');
            const highChartContainer = highchart.container;
            let newLabelYVal = labelYVal - 7;
            styles.NORMAL_STYLE.fontSize = 10;
            styles.NORMAL_STYLE.color = '#555555';
            const firstYAxisLabel = highChartContainer.getElementsByClassName('highcharts-yaxis-labels')[0].lastElementChild.getBoundingClientRect().right;
            const suffix = renderer.text(unitStr, +label.getAttribute('x'), +newLabelYVal)
              .css(styles.NORMAL_STYLE)
              .add();
            suffix.element.setAttribute('class', 'units y-axis-suffix');
            const suffixRight = suffix.element.getBoundingClientRect().right;
            const currentSuffX = suffix.element.getAttribute('x');
            const diff = suffixRight - firstYAxisLabel;
            suffix.element.setAttribute('x', currentSuffX - diff);

            // suffix.element.setAttribute('x', currentSuffX - diff);
            break;
          } else {
            label.innerHTML = label.innerHTML + '<tspan>' + unitStr + '</tspan>';
            break;
          }

        } else {
          let css = styles.NORMAL_STYLE;

          if (__WSJ__) {
            if (options.product === 'wsj') {
              css = Object.assign({}, css, {
                fontSize:  styles.AXES.label.fontSize,
                color: styles.AXES.label.color,
                fill: styles.AXES.label.color
              });
            }
          }

          const suffix = renderer.text(unitStr, +label.getAttribute('x') + xOffset, +label.getAttribute('y'))
            .css(css)
            .add();
          const currentY = suffix.element.getAttribute('y');
          suffix.element.setAttribute('class', 'units y-axis-suffix');
          // aligns the suffix with the label
          if (!options.print) {
            suffix.element.setAttribute('y', +currentY + .5);
          }
          break;
        }
      }
    }
  }

}

// On vertical bar / column charts, insert a small '0' in place of bars that
// have a zero value, so that it does not look like an error.
function insertZeroesForEmptyBars(renderer, axis, options) {
  const typeOk = options.type === 'column';
  const stackOk = !options.stacking || axis.series.length === 1;
  if (typeOk && stackOk) {
    const series = axis.series;
    const allLessThanZero = allValuesLessThanZero(axis);
    let g = [];
    axis.series.forEach(series => {
      series.data.forEach(item => {
        if (item.y === 0) {
          const el = item.graphic.element;
          const translate = transform.read(el.parentElement, 'translate');
          let x = +el.attributes.x.value + translate[0];
          let y = +el.attributes.y.value + translate[1];
          let smallStyle = options.styles.get('small');
          if (options.product === 'wsj') {
            smallStyle.color = '#555555';
          } else {
            smallStyle.color = item.color;
          }
          x += +el.attributes.width.value / 2;
          y += allLessThanZero ? parseFloat(options.styles.get('small').fontSize) : -4;
          const text = renderer.text('0', x, y)
            .attr({ 'text-anchor': 'middle' })
            .css(smallStyle)
            .add();
          g.push({ type: '0', el: text.element });
        } else if (item.graphic) {
          g.push({ type: 'bar', el: item.graphic.element });
        }
      });
    });

    // Sort by `left` position.
    g.sort(function (a, b) {
      return a.el.getBoundingClientRect().left -
        b.el.getBoundingClientRect().left;
    });

    // Check if 0s overlap bars or other 0s and, if so, remove.
    for (let i = 0; i < g.length - 1; i++) {
      if (g[i].type === '0') {
        const r1 = g[i].el.getBoundingClientRect();
        const r2 = g[i + 1].el.getBoundingClientRect();
        if (r1.right + 2 > r2.left) {
          element.removeChild(g[i].el);
        }
      }
    }
  }
}

// Returns true if all values are <= 0.
function allValuesLessThanZero(axis) {
  return axis.series.every(series => {
    return series.data.every(item => {
      return item.y <= 0;
    })
  });
}

// In Highcharts, grid lines ares places under lines and columns, which is
// fine. But we want the 0 line to be above (as in z or depth).
function moveZeroLineToTop(container, axis, options) {
  const zero = axis.ticks[0];
  if (zero) {
    const el = zero.gridLine.element;
    if (__WSJ__) {
      if (options.size === 'print-wsj') {
        const d = transform.read(el, 'd');
        if (options.type === 'bar') {
          d[0][0] = Math.floor(d[0][0]) - 0.5;
          d[1][0] = d[0][0];
        } else {
          d[0][1] = Math.ceil(d[0][1]);
          d[1][1] = d[0][1];
        }
        transform.write(el, 'd', d);
      }
    }
    container.querySelector('.highcharts-series-group').appendChild(el);
  }
}

// Sometimes Highcharts lets labels slip off the edge of chart, as if it
// measured the label width using a short label. Push the labels over so they
// all fit.
function ensureLabelsFit(axis, options, highchart) {
  // ensureHorizontalLabelsFit(axis);
  if (options.type === 'bar') {
    if (!options.axes.reversed) {
      ensureHorizontalLabelsFit(axis);
      
    }
  } else {
    ensureVerticalLabelsFit(axis, options, highchart);
  }
}

function ensureHorizontalLabelsFit(axis) {
  const key = axis.tickPositions[axis.tickPositions.length - 1];
  const tick = axis.ticks[key];
  const el = tick.label.element;
  const x2 = tick.label.xy.x + el.getBoundingClientRect().width / 2;
  const dif = axis.left + axis.width - x2;
  tick.label.xy.x += dif;
  if (dif < 0) {
    el.setAttribute('x', +el.getAttribute('x') + dif);
  }
}

function ensureVerticalLabelsFit(axis, options, highchart) {
  let maxWidth = 0;
  forAxisTicks(axis, tick => {
    const width = tick.label.element.getBoundingClientRect().width;
    maxWidth = Math.max(maxWidth, width);
  });  
  maxWidth = Math.ceil(maxWidth);
  if (options.product !== "marketwatch" && options.product !== 'financialnews') {
    forAxisTicks(axis, tick => {
      tick.label.element.setAttribute('x', maxWidth);
    });
  }
  else {
    if (__FINANCIALNEWS__) {
      if (options.product === 'financialnews') {
        if (options.type === 'line' || options.type === 'column') {
          const highChartContainer = highchart.container;
          let labelsG = highChartContainer.getElementsByClassName('highcharts-yaxis-labels');
          let translateValue = options.print === true ? 'translate(0, 5)' : 'translate(0, 6)';
          labelsG[0].attributes.transform.nodeValue = translateValue;
          const gElRectLabels = highChartContainer.getElementsByClassName('highcharts-yaxis-labels')[0];
          const gElRectRightLabels = gElRectLabels.getBoundingClientRect().right;
          const gElRectLabelsChildren = gElRectLabels.children;
          const horizontalGridLineArr = highChartContainer.getElementsByClassName('highcharts-yaxis-grid')[0].children;
          const gWidth = gElRectLabels.getBoundingClientRect().width;
          if (options.type === 'line') {
            // translate the yaxis labels to the right based on width of surround g element
            gElRectLabels.setAttribute('transform', 'translate(' + gWidth + ',' + '6)' );
            for (let i = 0; i < gElRectLabelsChildren.length; i++) {
              gElRectLabelsChildren[i].setAttribute('text-anchor', 'end');
              // if there's a 0 in yaxis labels remove the extra space/zero
              const hasZero = gElRectLabels.children[i].innerHTML === '0' || gElRectLabels.children[i].innerHTML[1] === '<' ? true : false;
              if (hasZero) {
                gElRectLabels.children[i].children[0].innerHTML = '';
              }
              const currentXVal = Number(gElRectLabelsChildren[i].getAttribute('x'));
              const dValue = transform.read(horizontalGridLineArr[i], 'd');
              let lastHorizontalGridLineElD = horizontalGridLineArr[i].getAttribute('d').split(' ');
              const childWidth = gElRectLabelsChildren[i].getBoundingClientRect().width;
              // extend horizontal gridlines to match position of label, which is now right aligned. highcharts positions them to be left aligned and all horizontal gridlines end at some point, thereby having same length
              lastHorizontalGridLineElD[3] = ((currentXVal + (gWidth) - childWidth - 2)) + "," + dValue[1][1];
              // if first yaxis label has a percentage sign, decrease length of horizontal gridline to account for extra space that % creates
              lastHorizontalGridLineElD[3] = (options.axes.units.suffix === '%') && i === gElRectLabelsChildren.length - 1 ? ((currentXVal + (gWidth) - childWidth - 15)) + "," + dValue[1][1] : lastHorizontalGridLineElD[3];
              // make sure first gridline has proper length
              lastHorizontalGridLineElD[3] = options.axes.units.prefix !== "" && i === gElRectLabelsChildren.length - 1 ? (currentXVal - (gWidth * 0.125)) + "," + dValue[1][1] : lastHorizontalGridLineElD[3];
              lastHorizontalGridLineElD[2] = '';
              lastHorizontalGridLineElD = lastHorizontalGridLineElD.join(' ');
              horizontalGridLineArr[i].setAttribute('d', lastHorizontalGridLineElD);
            }
          }
        }
        if (options.type !== 'line' && options.type !== 'column') {
          forAxisTicks(axis, tick => {
            tick.label.element.setAttribute('x', maxWidth);
          });
        }
      }
    }
    if (__MARKETWATCH__) {
      if (options.type === 'line' && options.product === 'marketwatch') {
        const highChartContainer = highchart.container;
        const xAxisGrid = highChartContainer.getElementsByClassName('highcharts-xaxis-grid')[0];
        const lastVerticalGridLineEl = xAxisGrid.lastElementChild;
        const firstVerticalGridLineEl = xAxisGrid.firstElementChild;
        let horizontalGridLineArr = highChartContainer.getElementsByClassName('highcharts-yaxis-grid')[0].children;
        // x value of last point
        const lastPoint = !options.irregularDates ? highchart.xAxis[0].series[0].data[highchart.xAxis[0].series[0].data.length - 1].clientX : options.series[0].data[options.series[0].data.length - 1].x;
        // x value of first point
        const firstPoint = !options.irregularDates ? highchart.xAxis[0].series[0].data[0].clientX : options.series[0].data[0].x;
        const borderCoord = document.getElementsByClassName('highcharts-plot-background')[1].getBoundingClientRect();
        // get first D values of first and last vertical grid lines
        let splitVerticalGridLineElCheck = lastVerticalGridLineEl.getAttribute('d').split(/(?=[LMC])/);
        let splitVerticalGridLineElFirstCheck = firstVerticalGridLineEl.getAttribute('d').split(/(?=[LMC])/);
        splitVerticalGridLineElFirstCheck = Math.round(splitVerticalGridLineElFirstCheck[0].split(' ')[1]);
        splitVerticalGridLineElCheck = Math.round(splitVerticalGridLineElCheck[1].split(' ')[1]);
        const roundFirstPoint = Math.round(firstPoint);
        const roundLastPoint = Math.round(lastPoint);
        // find the difference between first/last x point and d values
        const diffBetweenFirstAndGridEl = roundFirstPoint - splitVerticalGridLineElFirstCheck;
        const diffBetweenLastAndGridEl = roundLastPoint - splitVerticalGridLineElCheck;
        let splitVerticalGridLineElFirst = transform.read(firstVerticalGridLineEl, 'd')[0][0];

        // if first vertical gridline diff between first x point and first vertical grid line d value is negative AND
        // if diff between last vertical gridline x point and d value is greater than one, draw a border around chart and extend
        // horizontal gridlines to that border
        let comparisonValue = -5;
        if (options.size === 'marketwatch-ZR' || options.size === 'marketwatch-ZT' || options.size === 'marketwatch-4u-inset') {
          comparisonValue = 0;
        }
        if (diffBetweenFirstAndGridEl < comparisonValue && diffBetweenLastAndGridEl >= 0 ) {

          for (var i = 0; i < horizontalGridLineArr.length; i++) {
            let dValue = transform.read(horizontalGridLineArr[i], 'd');
            let lastHorizontalGridLineElD = horizontalGridLineArr[i].getAttribute('d').split(' ');
            if (firstPoint < splitVerticalGridLineElFirstCheck) {
              lastHorizontalGridLineElD[1] = (firstPoint - 1).toString() + ',' + dValue[0][1].toString();
            } else {
              lastHorizontalGridLineElD[1] = (splitVerticalGridLineElFirst - 0.5).toString() + ',' + dValue[0][1].toString();
            }
            lastHorizontalGridLineElD[3] =  (lastPoint + 1).toString() + "," + dValue[1][1];
            lastHorizontalGridLineElD[2] = '';
            lastHorizontalGridLineElD = lastHorizontalGridLineElD.join(' ');
            horizontalGridLineArr[i].setAttribute('d', lastHorizontalGridLineElD);
            if (i === 0 || i === horizontalGridLineArr.length - 1 ) {
              horizontalGridLineArr[i].setAttribute('stroke-width', 2);
            }
          }
          addLastVerticalGridline(lastVerticalGridLineEl, firstVerticalGridLineEl, firstPoint, lastPoint, highchart, xAxisGrid, false);
          addFirstVerticalGridline(firstVerticalGridLineEl, lastVerticalGridLineEl, lastPoint, firstPoint, highchart, xAxisGrid, false);
        }
        // extend horizontal gridlines to first and last x value
        else {
          for (var i = 0; i < horizontalGridLineArr.length; i++) {
            let dValue = transform.read(horizontalGridLineArr[i], 'd');
            let lastHorizontalGridLineElD = horizontalGridLineArr[i].getAttribute('d').split(' ');
            if (firstPoint < splitVerticalGridLineElFirstCheck) {
              lastHorizontalGridLineElD[1] = (firstPoint - 1).toString() + ',' + dValue[0][1].toString();
            } else {
              lastHorizontalGridLineElD[1] = firstPoint - splitVerticalGridLineElFirstCheck >= 5 ? firstPoint.toString() + ',' + dValue[0][1].toString() : splitVerticalGridLineElFirst.toString() + ',' + dValue[0][1].toString();
            }
            lastHorizontalGridLineElD[3] =  (lastPoint + 1).toString() + "," + dValue[1][1];
            lastHorizontalGridLineElD[2] = '';
            lastHorizontalGridLineElD = lastHorizontalGridLineElD.join(' ');
            horizontalGridLineArr[i].setAttribute('d', lastHorizontalGridLineElD);
            if (i === 0 || i === horizontalGridLineArr.length - 1 ) {
              horizontalGridLineArr[i].setAttribute('stroke-width', 2);
            }
          }
          // if diff between first x point and first d value is less than or equal to one AND
          // diff between last x point and last d value is greater one, then add a vertical gridline to end of chart
          if (diffBetweenLastAndGridEl > 1  || !options.categoriesAreDates) {
            addLastVerticalGridline(lastVerticalGridLineEl, firstVerticalGridLineEl, firstPoint, lastPoint, highchart, xAxisGrid, true)
          }
          if (diffBetweenLastAndGridEl < 1 && diffBetweenLastAndGridEl > 0) {
            // if diff between first x and first d value is greater than one, draw vertical gridline at beginning of chart
            if (firstPoint < splitVerticalGridLineElFirstCheck) {
              addFirstVerticalGridline(firstVerticalGridLineEl, lastVerticalGridLineEl, lastPoint, firstPoint, highchart, xAxisGrid, true);
            }
          }
          if (diffBetweenLastAndGridEl === 0 || diffBetweenFirstAndGridEl === 0) {
            if (Math.round(firstPoint) > splitVerticalGridLineElFirstCheck) {

              thickenFirstLine(firstVerticalGridLineEl, splitVerticalGridLineElFirstCheck, true);
              thickenLastLine(lastVerticalGridLineEl, lastPoint, true);
            } else {
              thickenLastLine(lastVerticalGridLineEl, lastPoint, false);
              thickenFirstLine(firstVerticalGridLineEl, firstPoint, false);
            }
          }
        }

        let labelsG = highChartContainer.getElementsByClassName('highcharts-yaxis-labels');
        let translateValue = 'translate(0, 6)';
        labelsG[0].attributes.transform.nodeValue = translateValue;
      }
      if (options.type === 'column' && options.product === 'marketwatch') {
        const highChartContainer = highchart.container;
        let labelsG = highChartContainer.getElementsByClassName('highcharts-yaxis-labels');
        let translateValue = 'translate(0, 6)';
        labelsG[0].attributes.transform.nodeValue = translateValue;
      }

      if (options.type !== "line" && options.type !== 'column') {
        forAxisTicks(axis, tick => {
          tick.label.element.setAttribute('x', maxWidth);
        });
      }
    }
  }

}

function shiftLastHorizontalBarLine(highchart, options) {
  const axis = highchart.axes[1];
  forAxisTicksReverse(axis, tick => {
    if (tick.isLast) {
      const d = transform.read(tick.gridLine.element, 'd');
      d[0][0] -= 1;
      d[1][0] = d[0][0];
      transform.write(tick.gridLine.element, 'd', d);
      // break:
      return false;
    }
  });
}

function addFirstVerticalGridline(firstVerticalGridLineEl, lastVerticalGridLineEl, lastPoint, firstPoint, highchart, xAxisGrid, thickenOpposite) {
  const newFirstPath = firstVerticalGridLineEl;
  let newFirstPathD = newFirstPath.getAttribute('d').split(' ');
  newFirstPathD[1] = firstPoint.toString();
  newFirstPathD[4] = firstPoint.toString();
  const newFirstPathStroke = newFirstPath.getAttribute('stroke');
  const newFirstPathOpacity = newFirstPath.getAttribute('opacity');
  let elementPathFirst = highchart.renderer.path(newFirstPathD).attr({
    stroke: newFirstPathStroke,
    fill: 'none',
    'stroke-width': 2,
    class: 'add-vertical-gridline',
    opacity: newFirstPathOpacity
  });
  xAxisGrid.insertBefore(elementPathFirst.element, firstVerticalGridLineEl);
  if (thickenOpposite) {
    thickenLastLine(lastVerticalGridLineEl, lastPoint);
  }
}

function addLastVerticalGridline(lastVerticalGridLineEl, firstVerticalGridLineEl, firstPoint, lastPoint, highchart, xAxisGrid, thickenOpposite) {
  const newLastPath = lastVerticalGridLineEl;
  let newLastPathD = newLastPath.getAttribute('d').split(' ');
  const newLastPathStroke = newLastPath.getAttribute('stroke');
  const newLastPathOpacity = newLastPath.getAttribute('opacity');
  newLastPathD[4] = lastPoint.toString();
  newLastPathD[1] = lastPoint.toString();
  let elementPath = highchart.renderer.path(newLastPathD).attr({
    stroke: newLastPathStroke,
    fill: 'none',
    'stroke-width': 2,
    class: 'add-vertical-gridline',
    opacity: newLastPathOpacity
  });
  xAxisGrid.appendChild(elementPath.element);
  if (thickenOpposite) {
    thickenFirstLine(firstVerticalGridLineEl, firstPoint);
  }
}

function thickenFirstLine(firstVerticalGridLineEl, firstPoint) {
  const newFirstPath = firstVerticalGridLineEl;
  let newFirstPathD = newFirstPath.getAttribute('d').split(' ');
  let newFirstPathDSecond = Number(newFirstPathD[2]);
  let newFirstPathLast = Number(newFirstPathD[newFirstPathD.length - 1]);
  newFirstPathD[1] = firstPoint.toString();
  newFirstPathD[4] = firstPoint.toString();
  newFirstPathD = newFirstPathD.join(' ');
  firstVerticalGridLineEl.setAttribute('d', newFirstPathD);
  firstVerticalGridLineEl.setAttribute('stroke-width', 2);
}

function thickenLastLine(lastVerticalGridLineEl, lastPoint) {
  const newLastPath = lastVerticalGridLineEl;
  let newLastPathD = newLastPath.getAttribute('d').split(' ');
  const newLastPathStroke = newLastPath.getAttribute('stroke');
  const newLastPathOpacity = newLastPath.getAttribute('opacity');
  let newLastPathDSecond = Number(newLastPathD[2]);
  let newLastPathLast = Number(newLastPathD[newLastPathD.length - 1]);
  newLastPathD[4] = lastPoint.toString();
  newLastPathD[1] = lastPoint.toString();
  newLastPathD = newLastPathD.join(' ');
  lastVerticalGridLineEl.setAttribute('d', newLastPathD);
  lastVerticalGridLineEl.setAttribute('stroke-width', 2);
}

function moveTopLabel(axis, options){
  const isColumn = options.type === 'column';
  const isPrint = options.size === 'print-wsj'
  const is8u = options.size === '8u';

  for (let tick in axis.ticks) {
    if(axis.ticks[tick].isLast){
      const topTick = axis.ticks[tick];
      const label = axis.ticks[tick].label.element;
      const position = +label.getAttribute('y');
      let offset = -2;

      if(is8u){
        offset = -1;
      }

      if(isColumn){
        offset = -1;
      }

      if(isPrint){
        offset = -.5;
      }

      const newPostition = position + offset;
      label.setAttribute('y', newPostition);
    }
  }
}


// WEBPACK FOOTER //
// ./src/chart/inner/axes-postrender-y-axis.js